// Client-only capture of Meta's ad-click match signals (`_fbc`, `_fbp`) for the server Conversions
// API, raising their coverage on the consent-gated web CAPI events. Consent-safe: nothing is written
// to the visitor's device before a consent decision (no cookie AND no Web Storage — ePrivacy Art.
// 5(3) governs the act of storage, not only transmission). Once consent is granted, `_fbc` is
// materialized from the `fbclid` still in the current URL. See the PR for the coverage trade-off and
// the SSR-threading follow-up option.

import { readConsent, readCookie } from "./consentAnalytics";

const FBC_COOKIE = "_fbc";
const FBP_COOKIE = "_fbp";

// Meta's default cookie window for fbc/fbp is 90 days. (Safari's ITP caps a JS-set cookie at 7 days
// regardless, but Meta's click-attribution window is also 7 days, so no funnel loss.)
const CLICK_ID_MAX_AGE = 60 * 60 * 24 * 90;

// Meta's fbclid is a URL-safe token (letters, digits, `_`, `-`). Validate it before it ever reaches
// a cookie value: this rejects a crafted `?fbclid=x;domain=…` that would otherwise inject cookie
// attributes via `document.cookie`, and guarantees the value never contains the `.` we use as the
// fbc field separator (which would corrupt `fbcClickId` parsing). Bounded length as a second guard.
const FBCLID_PATTERN = /^[A-Za-z0-9_-]{1,400}$/;

function safeFbclid(raw: string | null | undefined): string | null {
  return raw && FBCLID_PATTERN.test(raw) ? raw : null;
}

// The Meta Pixel scopes `_fbc`/`_fbp` to the registrable domain (`.folioai.com`), not the exact host,
// so `www.` and the apex share one cookie. Match that scope: writing host-only would create a second
// `_fbc` beside the Pixel's, and a delete would then miss the Pixel's copy. Folio serves a single-label
// TLD (folioai.com), so the last two labels are the registrable domain. Returns null for
// localhost / an IP (host-only), where a Domain attribute is invalid or pointless. Takes the hostname
// as an argument so the derivation (the one branch that only runs in prod) is unit-testable.
export function metaCookieDomain(hostname: string): string | null {
  if (hostname === "localhost" || /^[\d.]+$/.test(hostname)) return null;
  const labels = hostname.split(".");
  // A single-label host (e.g. a bare `folio-qa`) has no registrable domain; a `.folio-qa` Domain
  // attribute is invalid and the browser drops the write. Stay host-only there.
  if (labels.length < 2) return null;
  if (labels.length === 2) return `.${hostname}`;
  return `.${labels.slice(-2).join(".")}`;
}

function cookieAttributes(): string {
  const domain = metaCookieDomain(window.location.hostname);
  // `secure` only over https; omitting it on http (local prod-build QA) is what lets the browser
  // store the cookie there.
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  const domainAttr = domain ? `; domain=${domain}` : "";
  return `; path=/; max-age=${CLICK_ID_MAX_AGE}; samesite=lax${domainAttr}${secure}`;
}

function writeCookie(name: string, value: string) {
  document.cookie = `${name}=${value}${cookieAttributes()}`;
}

function deleteCookie(name: string) {
  // Delete BOTH scopes: a cookie written host-only is a distinct cookie from a domain-scoped one, so
  // a single domain-scoped delete can miss the Pixel's copy (or vice-versa) and leave a withdrawing
  // visitor still carrying the identifier.
  const domain = metaCookieDomain(window.location.hostname);
  document.cookie = `${name}=; path=/; max-age=0`;
  if (domain) document.cookie = `${name}=; path=/; max-age=0; domain=${domain}`;
}

// A well-formed `_fbc` is `fb.<subdomainIndex>.<creationMs>.<fbclid>`; `_fbp`'s last segment is a
// numeric id. We only need to know a value exists and has the right shape before deciding to keep it.
function isValidFbc(value: string | null): value is string {
  return !!value && /^fb\.\d+\.\d+\..+/.test(value);
}

function isValidFbp(value: string | null): value is string {
  return !!value && /^fb\.\d+\.\d+\.\d+$/.test(value);
}

// The fbclid is the 4th segment; used to detect a fresh, different ad click on a return visit.
function fbcClickId(value: string | null): string | null {
  if (!value) return null;
  const parts = value.split(".");
  return parts.length >= 4 ? parts.slice(3).join(".") : null;
}

function formatFbc(fbclid: string, ts: number): string {
  // fb.<subdomainIndex=1>.<creation time in MILLISECONDS>.<fbclid> — Meta expects ms, not seconds.
  return `fb.1.${ts}.${fbclid}`;
}

function generateFbp(): string {
  // fb.1.<creationMs>.<random>. Meta only needs a stable per-browser random suffix; a plain 10-digit
  // integer (1e9..1e10) matches the Pixel's own shape and stays inside Number.MAX_SAFE_INTEGER.
  const random = Math.floor(1e9 + Math.random() * 9e9);
  return `fb.1.${Date.now()}.${random}`;
}

function currentFbclid(): string | null {
  return safeFbclid(new URLSearchParams(window.location.search).get("fbclid"));
}

// Clear the click-id cookies — used when consent is denied or withdrawn, so a declining visitor is not
// left carrying ad-click identifiers. Ungated on purpose: clearing is always safe.
export function clearMetaClickIds() {
  if (typeof window === "undefined") return;
  deleteCookie(FBC_COOKIE);
  deleteCookie(FBP_COOKIE);
}

// Reconcile the click-id cookies with the current consent state + URL. Idempotent; safe to call on
// every funnel mount and again on a consent decision. No-op without a pixel id (nothing to match, so
// writing ad cookies would be pointless — keeps the feature inert without creds, like the rest of the
// consent/pixel plumbing) and no-op while consent is undecided (nothing is stored before consent).
export function syncMetaClickIds(pixelId: string | null) {
  if (typeof window === "undefined") return;

  const consent = readConsent();
  // Withdrawal must clear regardless of a pixel id (clearing is always safe), so it sits above the
  // pixel-id gate.
  if (consent === "denied") {
    clearMetaClickIds();
    return;
  }
  if (!pixelId) return;
  // Undecided: write NOTHING to the device. `_fbc` is materialized only once consent is granted.
  if (consent !== "granted") return;

  const existing = readCookie(FBC_COOKIE);
  const fbclid = currentFbclid();
  if (fbclid) {
    // Latest distinct click wins (the Pixel behaves the same); a repeat of the same click is a no-op.
    // Also rewrite when the existing value is malformed, even if its click-id segment happens to equal
    // the URL fbclid — a broken `_fbc` must not survive and keep riding CAPI events.
    if (!isValidFbc(existing) || fbcClickId(existing) !== fbclid) {
      writeCookie(FBC_COOKIE, formatFbc(fbclid, Date.now()));
    }
  } else if (existing && !isValidFbc(existing)) {
    // A malformed leftover `_fbc` with no click id to replace it would keep riding every CAPI event
    // and be rejected by Meta as a bad parameter; drop it so a missing fbc (better than a broken one)
    // is what CAPI sees.
    deleteCookie(FBC_COOKIE);
  }
  // Ensure a valid browser id exists so CAPI has `_fbp` even before the Pixel finishes loading. Repair
  // a malformed one for the same reason we repair `_fbc`: a broken `_fbp` rides every CAPI event.
  if (!isValidFbp(readCookie(FBP_COOKIE))) writeCookie(FBP_COOKIE, generateFbp());
}
